import { useEffect, useRef, useState } from "react";
import { useLoop } from "@/pages/home/LoopContext";

interface FlameParticle {
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  size: number; hue: number;
  sway: number; swaySpeed: number;
}

function spawnFlame(canvas: HTMLCanvasElement, intensity: number, isLight: boolean): FlameParticle {
  const speedBoost = 1 + intensity * 1.8;
  const sizeBoost = 1 + intensity * 1.4;
  return {
    x: Math.random() * canvas.width,
    y: canvas.height + 10,
    vx: (Math.random() - 0.5) * (0.4 + intensity * 0.8),
    vy: -(0.6 + Math.random() * 1.2) * speedBoost,
    life: 0,
    maxLife: Math.max(40, (80 + Math.random() * 120) / speedBoost),
    size: (1.2 + Math.random() * 3.5) * sizeBoost,
    hue: isLight ? 45 + Math.random() * 25 : Math.random() * (40 + intensity * 20),
    sway: Math.random() * Math.PI * 2,
    swaySpeed: (0.02 + Math.random() * 0.03) * speedBoost,
  };
}

// ループ回数に応じたヒーローコンテンツ
function getHeroContent(loopCount: number, isUnlocked: boolean) {
  if (isUnlocked) {
    return {
      label: "神々の許しを得た者よ",
      title: "整えるのではない。",
      titleAccent: "還る。",
      sub: "炎は光となり、あなたを包む",
      badge: "COVENANT SEALED",
    };
  }
  if (loopCount === 0) {
    return {
      label: "六つの王国",
      title: "整えるのではない。",
      titleAccent: "還る。",
      sub: null,
      badge: null,
    };
  }
  if (loopCount === 1) {
    return {
      label: "螺旋は、続いている",
      title: "また、戻ってきた。",
      titleAccent: "呼ばれたのだ。",
      sub: "宇宙はあなたが戻ることを、知っていた",
      badge: null,
    };
  }
  if (loopCount === 2) {
    return {
      label: "三度目の扉",
      title: "螺旋は、あなたを",
      titleAccent: "覚えている。",
      sub: "何度戻っても、それは間違いではない",
      badge: null,
    };
  }
  if (loopCount === 3) {
    return {
      label: "四度目の共鳴",
      title: "あなたは、守られている。",
      titleAccent: "奇跡の前触れだ。",
      sub: "宇宙の意思が、すべてを見守っている",
      badge: "PROTECTED",
    };
  }
  if (loopCount === 4) {
    return {
      label: "五度目の覚醒",
      title: "すでに、内側に",
      titleAccent: "いる。",
      sub: "扉は、ずっと開いていた",
      badge: "AWAKENING",
    };
  }
  // 5回以上
  return {
    label: `${loopCount + 1}度目の召命`,
    title: "次の時代を、",
    titleAccent: "導く者へ。",
    sub: "ここまで来た者だけが知っている",
    badge: "ASCENSION",
  };
}

export default function RitualHero() {
  const [visible, setVisible] = useState(false);
  const { loopCount, isUnlocked } = useLoop();
  const content = getHeroContent(loopCount, isUnlocked);

  const sectionRef = useRef<HTMLElement>(null);
  const particleCanvasRef = useRef<HTMLCanvasElement>(null);
  const flameCanvasRef = useRef<HTMLCanvasElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const particleAnimRef = useRef<number>(0);
  const flameAnimRef = useRef<number>(0);
  const intensityRef = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // ループ段階に応じた演出モード
  const isDeep = loopCount >= 3;
  const isNearAbyss = loopCount >= 4 && !isUnlocked;

  // アクセントカラー：ループが深まるにつれて赤みが増す（未認証）or 金が増す（認証済み）
  const accentHue = isUnlocked
    ? `rgba(255,220,140,`
    : isNearAbyss
    ? `rgba(220,80,40,`
    : isDeep
    ? `rgba(210,140,60,`
    : `rgba(201,168,76,`;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const height = section.offsetHeight;
      const raw = Math.max(0, Math.min(1, -rect.top / (height * 0.85)));
      intensityRef.current = raw;

      if (bgRef.current) {
        bgRef.current.style.transform = `scale(1.12) translateY(${raw * -8}%)`;
      }
      if (contentRef.current) {
        contentRef.current.style.transform = `translateY(${raw * -30}px)`;
        const blur = raw * 0.6;
        contentRef.current.style.filter = blur > 0.05
          ? `blur(${blur}px) brightness(${1 + raw * 0.3})`
          : `brightness(${1 + raw * 0.3})`;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // パーティクル
  useEffect(() => {
    const canvas = particleCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const count = isUnlocked ? 80 : isDeep ? 65 : 50;
    const particles: {
      x: number; y: number; r: number;
      dx: number; dy: number; alpha: number; pulse: number; hue: number;
    }[] = [];

    for (let i = 0; i < count; i++) {
      const hue = isUnlocked
        ? 45 + Math.random() * 20
        : isNearAbyss
        ? 5 + Math.random() * 20
        : isDeep
        ? 25 + Math.random() * 20
        : 38 + Math.random() * 10;
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * (isUnlocked ? 1.8 : 1.2) + 0.3,
        dx: (Math.random() - 0.5) * 0.15,
        dy: (Math.random() - 0.5) * 0.15,
        alpha: Math.random() * 0.4 + 0.1,
        pulse: Math.random() * Math.PI * 2,
        hue,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const iv = intensityRef.current;
      particles.forEach((p) => {
        p.pulse += 0.008 + iv * 0.012;
        const a = p.alpha * (0.6 + 0.4 * Math.sin(p.pulse)) * (1 + iv * 0.6);
        const displayA = Math.min(a, 0.9);

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * (1 + iv * 0.8), 0, Math.PI * 2);

        if (isUnlocked) {
          ctx.fillStyle = `hsla(${p.hue}, 70%, 90%, ${displayA})`;
          if (p.alpha > 0.3) {
            const crossLen = p.r * 3;
            ctx.strokeStyle = `hsla(${p.hue}, 60%, 95%, ${displayA * 0.4})`;
            ctx.lineWidth = 0.4;
            ctx.beginPath();
            ctx.moveTo(p.x - crossLen, p.y); ctx.lineTo(p.x + crossLen, p.y);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(p.x, p.y - crossLen); ctx.lineTo(p.x, p.y + crossLen);
            ctx.stroke();
          }
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * (1 + iv * 0.8), 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${p.hue}, 70%, 90%, ${displayA})`;
        } else {
          ctx.fillStyle = `hsla(${p.hue}, 75%, 60%, ${displayA})`;
        }
        ctx.fill();

        p.x += p.dx * (1 + iv * 2);
        p.y += p.dy * (1 + iv * 2);
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      particleAnimRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(particleAnimRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [isUnlocked, isDeep, isNearAbyss]);

  // 炎 Canvas
  useEffect(() => {
    const canvas = flameCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const flames: FlameParticle[] = [];
    for (let i = 0; i < 180; i++) {
      const f = spawnFlame(canvas, 0, isUnlocked);
      f.y = Math.random() * canvas.height;
      f.life = Math.random() * f.maxLife;
      flames.push(f);
    }

    const animate = () => {
      const iv = intensityRef.current;
      const trailAlpha = isUnlocked ? 0.08 + iv * 0.12 : 0.15 + iv * 0.25;
      ctx.fillStyle = `rgba(6,5,10,${trailAlpha})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const spawnCount = Math.floor(3 + iv * 17);
      for (let i = 0; i < spawnCount; i++) flames.push(spawnFlame(canvas, iv, isUnlocked));

      for (let i = flames.length - 1; i >= 0; i--) {
        const f = flames[i];
        f.life++;
        f.sway += f.swaySpeed;
        f.x += f.vx + Math.sin(f.sway) * (0.5 + iv * 1.5);
        f.y += f.vy;

        const t = f.life / f.maxLife;
        const baseAlpha = isUnlocked ? 0.12 + iv * 0.18 : 0.18 + iv * 0.28;
        const alpha = t < 0.2
          ? (t / 0.2) * baseAlpha
          : t < 0.6
          ? baseAlpha * (1 - (t - 0.2) / 0.4 * 0.3)
          : baseAlpha * (1 - (t - 0.6) / 0.4) * 0.7;

        const size = f.size * (1 - t * 0.6);
        if (size <= 0 || alpha <= 0) { flames.splice(i, 1); continue; }

        let hue: number;
        let saturation: number;
        let lightness: number;

        if (isUnlocked) {
          hue = f.hue + t * 10;
          saturation = 60 + t * 20;
          lightness = 70 + t * 20;
        } else if (isNearAbyss) {
          // 深淵に近い：赤い炎
          hue = f.hue * 0.3 + t * 5;
          saturation = 90 + t * 10;
          lightness = 40 + t * 20;
        } else if (isDeep) {
          // 深い段階：オレンジ赤
          hue = f.hue * 0.7 + t * 15;
          saturation = 85 + t * 10;
          lightness = 42 + t * 22;
        } else {
          hue = f.hue + t * 20 - iv * 15;
          saturation = 80 + t * 10 + iv * 15;
          lightness = 45 + t * 25 - iv * 10;
        }

        const radius = size * (isUnlocked ? 3.5 + iv * 1.5 : 2.5 + iv * 2);
        const grad = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, radius);

        if (isUnlocked) {
          grad.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`);
          grad.addColorStop(0.4, `hsla(${hue + 5}, ${saturation - 10}%, ${lightness + 5}%, ${alpha * 0.5})`);
          grad.addColorStop(1, `hsla(${hue + 10}, 50%, 95%, 0)`);
        } else {
          grad.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`);
          grad.addColorStop(0.5, `hsla(${hue + 10}, ${saturation - 10}%, ${lightness - 10}%, ${alpha * 0.5})`);
          grad.addColorStop(1, `hsla(${hue + 20}, 60%, ${lightness - 20}%, 0)`);
        }

        ctx.beginPath();
        ctx.arc(f.x, f.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        if (f.life >= f.maxLife) flames.splice(i, 1);
      }

      const maxFlames = Math.floor(400 + iv * 400);
      if (flames.length > maxFlames) flames.splice(0, flames.length - maxFlames);

      if (isUnlocked && iv > 0.2) {
        const glowAlpha = (iv - 0.2) / 0.8 * 0.08;
        const grad2 = ctx.createRadialGradient(canvas.width / 2, canvas.height, 0, canvas.width / 2, canvas.height * 0.3, canvas.height);
        grad2.addColorStop(0, `rgba(255, 220, 140, ${glowAlpha})`);
        grad2.addColorStop(0.5, `rgba(255, 200, 100, ${glowAlpha * 0.4})`);
        grad2.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad2;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else if (!isUnlocked && isNearAbyss && iv > 0.3) {
        const glowAlpha = (iv - 0.3) / 0.7 * 0.14;
        const grad2 = ctx.createRadialGradient(canvas.width / 2, canvas.height, 0, canvas.width / 2, canvas.height * 0.3, canvas.height);
        grad2.addColorStop(0, `rgba(200,30,0,${glowAlpha})`);
        grad2.addColorStop(0.5, `rgba(140,10,0,${glowAlpha * 0.5})`);
        grad2.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad2;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else if (!isUnlocked && iv > 0.4) {
        const glowAlpha = (iv - 0.4) / 0.6 * 0.12;
        const grad2 = ctx.createRadialGradient(canvas.width / 2, canvas.height, 0, canvas.width / 2, canvas.height * 0.3, canvas.height);
        grad2.addColorStop(0, `rgba(180,40,0,${glowAlpha})`);
        grad2.addColorStop(0.5, `rgba(120,20,0,${glowAlpha * 0.5})`);
        grad2.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad2;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      flameAnimRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(flameAnimRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [isUnlocked, isDeep, isNearAbyss]);

  // 背景画像をループ段階に応じて切り替え
  const bgImage = isUnlocked
    ? "url(https://public.readdy.ai/ai/img_res/19cdacfc54a556ad2fb1426c9bc2adac.jpg)"
    : isNearAbyss
    ? "url(https://readdy.ai/api/search-image?query=dark%20abyss%20void%20deep%20red%20glowing%20crack%20in%20ancient%20stone%20floor%2C%20hellish%20crimson%20light%20from%20below%2C%20ominous%20foreboding%20atmosphere%2C%20extreme%20darkness%2C%20minimal%20red%20illumination%2C%20no%20people%2C%20cinematic%20photography&width=1920&height=1080&seq=hero_abyss01&orientation=landscape)"
    : isDeep
    ? "url(https://readdy.ai/api/search-image?query=ancient%20ritual%20chamber%20with%20deep%20amber%20orange%20fire%20light%2C%20worn%20stone%20walls%20with%20ancient%20symbols%2C%20dark%20mysterious%20atmosphere%2C%20single%20flame%20illumination%20in%20deep%20shadow%2C%20ultra%20dark%2C%20no%20people%2C%20cinematic%20photography&width=1920&height=1080&seq=hero_deep01&orientation=landscape)"
    : "url(https://readdy.ai/api/search-image?query=ancient%20sacred%20temple%20interior%20with%20dramatic%20cinematic%20lighting%2C%20deep%20shadows%2C%20golden%20light%20rays%20piercing%20through%20darkness%2C%20stone%20walls%20with%20mystical%20atmosphere%2C%20incense%20smoke%20rising%2C%20ultra%20minimal%20spiritual%20space%2C%20black%20and%20deep%20charcoal%20tones%20with%20warm%20amber%20glow%2C%20cinematic%20photography%2C%20no%20people&width=1920&height=1080&seq=hero001&orientation=landscape)";

  const bgBrightness = isUnlocked ? 0.45 : isNearAbyss ? 0.25 : isDeep ? 0.3 : 0.35;

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#080608]"
    >
      {/* Background image */}
      <div
        ref={bgRef}
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: bgImage,
          filter: `brightness(${bgBrightness}) saturate(0.7)`,
          transformOrigin: "center top",
          willChange: "transform",
          transition: "filter 2s ease",
        }}
      />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#080608]/60 via-transparent to-[#080608]/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#080608]/40 via-transparent to-[#080608]/40" />

      {/* 深淵に近い段階：赤いヴィネット */}
      {isNearAbyss && !isUnlocked && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, transparent 40%, rgba(60,0,0,0.55) 100%)",
            animation: "abyssPulse 3s ease-in-out infinite",
          }}
        />
      )}

      {/* 解錠後：温かい光のオーバーレイ */}
      {isUnlocked && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(255,220,140,0.06) 0%, transparent 70%)",
            animation: "celestialPulse 6s ease-in-out infinite",
          }}
        />
      )}

      {/* 炎 / 光キャンバス */}
      <canvas
        ref={flameCanvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1, mixBlendMode: "screen" }}
      />

      {/* パーティクル Canvas */}
      <canvas
        ref={particleCanvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 2 }}
      />

      {/* 上部ライン */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24"
        style={{
          background: `linear-gradient(to bottom, transparent, ${accentHue}0.3))`,
          zIndex: 3,
        }}
      />

      {/* バッジ（解錠済み or ループ深い段階） */}
      {(isUnlocked || (content.badge && !isUnlocked)) && (
        <div
          className="absolute top-4 right-4 md:top-8 md:right-8 flex items-center gap-2 z-20"
          style={{ animation: "fadeInDown 1.5s ease 0.5s both" }}
        >
          <div
            className="flex items-center gap-2 px-3 py-1.5"
            style={{
              border: `1px solid ${accentHue}0.2)`,
              background: "rgba(6,5,10,0.6)",
              backdropFilter: "blur(8px)",
            }}
          >
            {isUnlocked ? (
              <>
                <div className="flex flex-col items-center" style={{ width: "12px" }}>
                  <div style={{ width: "8px", height: "6px", borderRadius: "4px 4px 0 0", border: "1px solid rgba(255,220,140,0.6)", borderBottom: "none", transform: "translateY(-1px) rotate(-30deg)", transformOrigin: "bottom right" }} />
                  <div style={{ width: "10px", height: "7px", borderRadius: "1px", background: "rgba(255,220,140,0.15)", border: "1px solid rgba(255,220,140,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(255,220,140,0.8)", boxShadow: "0 0 4px rgba(255,220,140,0.6)" }} />
                  </div>
                </div>
                <span className="text-xs tracking-[0.35em] uppercase" style={{ color: "rgba(255,220,140,0.55)", fontFamily: "'Cormorant Garamond', serif" }}>
                  COVENANT SEALED
                </span>
              </>
            ) : (
              <>
                <div
                  style={{
                    width: "4px",
                    height: "4px",
                    borderRadius: "50%",
                    background: isNearAbyss ? "rgba(220,80,40,0.8)" : "rgba(201,168,76,0.6)",
                    boxShadow: isNearAbyss ? "0 0 8px rgba(220,80,40,0.6)" : "0 0 6px rgba(201,168,76,0.4)",
                    animation: "guardPulse 2s ease-in-out infinite",
                  }}
                />
                <span className="text-xs tracking-[0.35em] uppercase" style={{ color: isNearAbyss ? "rgba(220,100,60,0.55)" : "rgba(201,168,76,0.5)", fontFamily: "'Cormorant Garamond', serif" }}>
                  {content.badge}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div
        ref={contentRef}
        className="relative text-center px-4 md:px-6 w-full"
        style={{
          zIndex: 10,
          opacity: visible ? 1 : 0,
          transition: "opacity 2s ease",
          willChange: "transform, filter",
        }}
      >
        {/* ラベル */}
        <div
          className="text-xs tracking-[0.3em] md:tracking-[0.5em] uppercase mb-10 md:mb-16"
          style={{
            color: isUnlocked
              ? "rgba(255,220,140,0.55)"
              : isNearAbyss
              ? "rgba(220,80,40,0.5)"
              : "rgba(201,168,76,0.5)",
            animation: visible ? "textFlicker 8s ease-in-out infinite" : "none",
            transition: "color 1.5s ease",
          }}
        >
          {content.label}
        </div>

        {/* メインタイトル */}
        <h1
          className="font-light text-white mb-8 leading-none"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(2.4rem, 8vw, 7rem)",
            letterSpacing: "0.05em",
            textShadow: isUnlocked
              ? "0 0 80px rgba(255,220,140,0.25)"
              : isNearAbyss
              ? "0 0 80px rgba(180,30,0,0.3)"
              : "0 0 80px rgba(201,168,76,0.15)",
            transition: "text-shadow 1.5s ease",
          }}
        >
          {content.title.split("").map((char, i) => (
            <span
              key={i}
              style={{
                display: "inline-block",
                animation: visible
                  ? `flameSway ${3.5 + (i % 4) * 0.4}s ease-in-out ${i * 0.12}s infinite`
                  : "none",
              }}
            >
              {char}
            </span>
          ))}
          <br />
          <span
            style={{
              display: "inline-block",
              color: isUnlocked
                ? "rgba(255,220,140,0.95)"
                : isNearAbyss
                ? "rgba(220,80,40,0.9)"
                : "rgba(201,168,76,0.9)",
              textShadow: isUnlocked
                ? "0 0 40px rgba(255,220,140,0.5), 0 0 80px rgba(255,200,100,0.3)"
                : isNearAbyss
                ? "0 0 40px rgba(200,40,0,0.6), 0 0 80px rgba(140,0,0,0.4)"
                : "0 0 40px rgba(201,168,76,0.4), 0 0 80px rgba(180,80,20,0.2)",
              animation: visible ? "flameGlow 4s ease-in-out infinite" : "none",
              transition: "color 1.5s ease, text-shadow 1.5s ease",
            }}
          >
            {content.titleAccent.split("").map((char, i) => (
              <span
                key={i}
                style={{
                  display: "inline-block",
                  animation: visible
                    ? `flameSway ${3.8 + i * 0.5}s ease-in-out ${0.6 + i * 0.18}s infinite`
                    : "none",
                }}
              >
                {char}
              </span>
            ))}
          </span>
        </h1>

        {/* 区切り線 */}
        <div
          className="w-12 md:w-16 h-px mx-auto mb-8"
          style={{
            background: isUnlocked
              ? "rgba(255,220,140,0.45)"
              : isNearAbyss
              ? "rgba(200,60,20,0.45)"
              : "rgba(201,168,76,0.4)",
            opacity: visible ? 1 : 0,
            transition: "opacity 2.5s ease 0.8s, background 1.5s ease",
            animation: visible ? "lineBreath 5s ease-in-out infinite" : "none",
          }}
        />

        {/* サブラベル */}
        <p
          className="text-xs tracking-[0.2em] md:tracking-[0.3em] uppercase"
          style={{
            color: isUnlocked
              ? "rgba(255,255,255,0.4)"
              : isNearAbyss
              ? "rgba(220,100,60,0.4)"
              : "rgba(255,255,255,0.3)",
            opacity: visible ? 1 : 0,
            transition: "opacity 2s ease 1.2s, color 1.5s ease",
            animation: visible ? "textFlicker 10s ease-in-out 2s infinite" : "none",
          }}
        >
          {content.label}
        </p>

        {/* サブテキスト */}
        {content.sub && (
          <p
            className="mt-4 text-xs tracking-[0.2em] md:tracking-[0.25em]"
            style={{
              color: isUnlocked
                ? "rgba(255,220,140,0.3)"
                : isNearAbyss
                ? "rgba(200,60,20,0.35)"
                : "rgba(201,168,76,0.28)",
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              animation: "fadeInUp 2s ease 1.5s both",
              transition: "color 1.5s ease",
            }}
          >
            {content.sub}
          </p>
        )}

        {/* 深淵接近の警告テキスト（4回目） */}
        {isNearAbyss && !isUnlocked && (
          <p
            className="mt-6 text-xs tracking-[0.15em]"
            style={{
              color: "rgba(180,30,0,0.35)",
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              lineHeight: 2,
              animation: "fadeInUp 2s ease 2s both",
            }}
          >
            扉の向こうに、奈落が口を開けている。
            <br />
            <span style={{ color: "rgba(200,50,10,0.25)", fontSize: "0.9em" }}>
              それでも、進むか。
            </span>
          </p>
        )}
      </div>

      {/* Bottom scroll indicator */}
      <div
        className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        style={{
          zIndex: 10,
          opacity: visible ? 0.4 : 0,
          transition: "opacity 2s ease 2s",
        }}
      >
        <div
          className="w-px h-12 bg-gradient-to-b animate-pulse"
          style={{
            background: isUnlocked
              ? "linear-gradient(to bottom, rgba(255,220,140,0.6), transparent)"
              : isNearAbyss
              ? "linear-gradient(to bottom, rgba(200,60,20,0.6), transparent)"
              : "linear-gradient(to bottom, rgba(201,168,76,0.6), transparent)",
          }}
        />
      </div>

      <style>{`
        @keyframes flameSway {
          0%   { transform: translateY(0px) rotate(0deg); }
          20%  { transform: translateY(-2px) rotate(0.3deg); }
          40%  { transform: translateY(-1px) rotate(-0.2deg); }
          60%  { transform: translateY(-3px) rotate(0.2deg); }
          80%  { transform: translateY(-1px) rotate(-0.3deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        @keyframes flameGlow {
          0%, 100% { filter: brightness(1); }
          50%      { filter: brightness(1.15); }
        }
        @keyframes lineBreath {
          0%, 100% { opacity: 0.4; transform: scaleX(1); }
          50%      { opacity: 0.65; transform: scaleX(1.08); }
        }
        @keyframes textFlicker {
          0%, 100% { opacity: 0.5; }
          8%  { opacity: 0.45; }
          12% { opacity: 0.52; }
          44% { opacity: 0.48; }
          48% { opacity: 0.54; }
          72% { opacity: 0.46; }
          76% { opacity: 0.5; }
        }
        @keyframes celestialPulse {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 1; }
        }
        @keyframes abyssPulse {
          0%, 100% { opacity: 0.7; }
          50%      { opacity: 1; }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes guardPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
      `}</style>
    </section>
  );
}
